import React, { useState } from 'react';
import { Languages, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../../store';
import { translateLocalizedValue, getMissingTargets, LANG_LABEL } from '../../lib/translateLocalized';

const LANGS = ['en', 'nl', 'ro', 'ru'];

/**
 * Compact 4-tab multilingual input. EN is required; the others are
 * optional and surface in the public UI via `getLocalized()` with EN fallback.
 *
 * A 1-click "Auto-translate" button (Claude Sonnet 4.5 via /api/admin/translate)
 * fills any empty NL/RO/RU tabs from the English source. Existing translations
 * are preserved — only blank tabs are overwritten.
 */
const LocalizedField = ({
  value,
  onChange,
  multiline = false,
  rows = 3,
  placeholder = '',
  testid,
  label,
  required = false,
}) => {
  const [active, setActive] = useState('en');
  const [translating, setTranslating] = useState(false);
  const token = useAuthStore((s) => s.token);
  const v = value || { en: '', nl: '', ro: '', ru: '' };

  const handle = (lang) => (e) => {
    onChange({ ...v, [lang]: e.target.value });
  };

  const sourceEn = (v.en || '').trim();
  const missingTargets = getMissingTargets(v);
  const canTranslateField = Boolean(sourceEn) && missingTargets.length > 0;

  const handleAutoTranslate = async () => {
    if (!canTranslateField || !token) return;
    setTranslating(true);
    try {
      const { value: next, filled, targets } = await translateLocalizedValue(token, v);
      onChange(next);
      if (filled > 0) {
        toast.success(`Translated to ${targets.map((l) => LANG_LABEL[l]).join(', ')}`);
      } else {
        toast.error('Translator returned empty result');
      }
    } catch (err) {
      toast.error(err.message || 'Auto-translate failed');
    } finally {
      setTranslating(false);
    }
  };

  const baseClass = 'w-full px-3 py-2 border border-border bg-white text-neutral-900 text-sm focus:outline-none focus:border-foreground' + (multiline ? ' resize-y' : '');

  return (
    <div data-testid={testid}>
      {label && (
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
            {label}{required && ' *'}
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleAutoTranslate}
              disabled={!canTranslateField || translating}
              title={
                !sourceEn
                  ? 'Enter English text first'
                  : missingTargets.length === 0
                    ? 'All languages already filled'
                    : `Translate EN → ${missingTargets.map((l) => LANG_LABEL[l]).join(', ')}`
              }
              className={`flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-wider uppercase border transition-colors ${
                canTranslateField && !translating
                  ? 'border-foreground/40 text-foreground hover:bg-foreground hover:text-background cursor-pointer'
                  : 'border-border text-muted-foreground/50 cursor-not-allowed'
              }`}
              data-testid={`${testid}-auto-translate`}
            >
              {translating
                ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />
                : <Languages className="w-3 h-3" strokeWidth={1.5} />}
              {translating ? 'Translating…' : 'Auto-translate'}
            </button>
            <div className="flex items-center gap-0.5" role="tablist" aria-label="Language">
              {LANGS.map((lang) => {
                const hasValue = Boolean((v[lang] || '').trim());
                return (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setActive(lang)}
                    role="tab"
                    aria-selected={active === lang}
                    className={`px-2 py-0.5 text-[10px] tracking-wider uppercase border transition-colors ${
                      active === lang
                        ? 'border-foreground text-foreground bg-foreground/5'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                    data-testid={`${testid}-tab-${lang}`}
                    title={lang === 'en' ? 'English (required)' : `${LANG_LABEL[lang]} translation`}
                  >
                    {LANG_LABEL[lang]}{hasValue && <span className="ml-1 text-emerald-600">•</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {multiline ? (
        <textarea
          rows={rows}
          value={v[active] || ''}
          onChange={handle(active)}
          placeholder={placeholder || (active === 'en' ? 'English content (required)' : `${LANG_LABEL[active]} translation (optional)`)}
          className={baseClass}
          data-testid={`${testid}-${active}`}
          required={required && active === 'en'}
        />
      ) : (
        <input
          type="text"
          value={v[active] || ''}
          onChange={handle(active)}
          placeholder={placeholder || (active === 'en' ? 'English (required)' : `${LANG_LABEL[active]} (optional)`)}
          className={baseClass}
          data-testid={`${testid}-${active}`}
          required={required && active === 'en'}
        />
      )}
    </div>
  );
};

export default LocalizedField;
